import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import { getRolehasPermissions } from "../server/rolesAllApi";

const MasterLayout = ({ children }) => {
  const [sidebarActive, setSidebarActive] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userPermissions, setUserPermissions] = useState([]);
  const [roleName, setRoleName] = useState("");
  const [token, setToken] = useState("");
  const [batchNames, setBatchNames] = useState([]);
  const [error, setError] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null); // Track open dropdown
  const [userName, setUserName] = useState("");
  const [name, setName] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  // Initial token and user check
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    if (!user || !storedToken) {
      navigate("/");
      return;
    }
    const parsedUser = JSON.parse(user);
    setRoleName(parsedUser?.rolename);
    setToken(storedToken);
    const userEmail = parsedUser?.userName;

    setUserName(userEmail);
    setName(parsedUser?.name);
    fetchUserDetails();
    fetchBatches();
  },[token]);

  // Fetch user permissions based on role

  const fetchUserDetails = async () => {
    if (!token) return;
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) return;

    setRoleName(user?.rolename);
    if (user?.roleId) {
      try {
        const response = await getRolehasPermissions(user?.roleId);
        setUserPermissions(response?.data || []);
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
    }
    setLoading(false);
  };

  // Fetch batch names for Course Verification
  const fetchBatches = async () => {
    if (!token) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_INSTITUTION_BASEURL}/get-all-batch`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      setBatchNames(data?.data || []);
    } catch (err) {
      setError("Failed to fetch batch names");
      console.error(err);
    }
  };

  // Sidebar and mobile menu toggles
  const sidebarControl = () => setSidebarActive(!sidebarActive);
  const mobileMenuControl = () => setMobileMenu(!mobileMenu);

  // Logout handler
  const handleLogout = (e) => {
    e.preventDefault();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("userPermissions");
    navigate("/");
  };

  // Toggle dropdown
  const toggleDropdown = (dropdownId) => {
    setOpenDropdown(prev => prev === dropdownId ? null : dropdownId);
  };

  // Add this new function to close dropdowns
  const closeDropdowns = () => {
    setOpenDropdown(null);
  };

  // Handle submenu click to keep dropdown open
  const handleSubmenuClick = (e, dropdownId) => {
    setOpenDropdown(dropdownId); // Keep the dropdown open
  };

  // Helper function to check if a dropdown has any permitted subitems
  const hasPermissionForDropdown = (permissions) => {
    return permissions.some((perm) => userPermissions.includes(perm));
  };

  // Check if the current path is "/chat-view"
  const isChatView = location.pathname === "/chat-view";

  return (
    <section className={mobileMenu ? "overlay active" : "overlay"}>
      <aside
        className={
          sidebarActive
            ? "sidebar active"
            : mobileMenu
            ? "sidebar sidebar-open"
            : "sidebar"
        }
      >
        <button
          onClick={mobileMenuControl}
          type="button"
          className="sidebar-close-btn"
        >
          <Icon icon="radix-icons:cross-2" />
        </button>
        <div>
          <Link to="/dashboard" className="sidebar-logo">
            <img
              src="/assets/images/Logo/Educate 2 trade_Dark.png"
              alt="site logo"
              className="light-logo"
            />
            <img
              src="/assets/images/Logo/Educate 2 trade.png"
              alt="site logo"
              className="dark-logo"
            />
            <img
              src="/assets/images/Logo/Logo ICon_Dark.png"
              alt="site logo"
              className="logo-icon"
            />
          </Link>
        </div>
        <div className="sidebar-menu-area">
          <ul className="sidebar-menu" id="sidebar-menu">
            <li>
              <NavLink
                to="/dashboard"
                className={({ isActive }) => (isActive ? "active-page" : "")}
                onClick={closeDropdowns}
              >
                <Icon
                  icon="solar:home-smile-angle-outline"
                  className="menu-icon"
                />
                <span>Dashboard</span>
              </NavLink>
            </li>

            {hasPermissionForDropdown(["role.index", "assignRole.index"]) && (
              <li
                className={`dropdown ${openDropdown === "roles" ? "open" : ""}`}
              >
                <Link to="#" onClick={() => toggleDropdown("roles")}>
                  <Icon icon="mdi:account-cog-outline" className="menu-icon" />
                  <span>Roles</span>
                </Link>
                <ul className="sidebar-submenu">
                  {userPermissions.includes("role.index") && (
                    <li>
                      <NavLink
                        to="/role-access"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "roles")}
                      >
                        <i className="ri-circle-fill circle-icon text-primary-600 w-auto" />{" "}
                        Roles
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("assignRole.index") && (
                    <li>
                      <NavLink
                        to="/assign-role"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "roles")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Assign Role
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            )}
            {hasPermissionForDropdown([
              "department.index",
              "level.index",
              "course.index",
              "chapters.index",
              "units.index",
            ]) && (
              <li
                className={`dropdown ${
                  openDropdown === "academics" ? "open" : ""
                }`}
              >
                <Link to="#" onClick={() => toggleDropdown("academics")}>
                  <Icon icon="mdi:school-outline" className="menu-icon" />
                  <span>Academics</span>
                </Link>
                <ul className="sidebar-submenu">
                  {userPermissions.includes("department.index") && (
                    <li>
                      <NavLink
                        to="/department-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "academics")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Departments
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("level.index") && (
                    <li>
                      <NavLink
                        to="/level-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "academics")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Levels
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("course.index") && (
                    <li>
                      <NavLink
                        to="/courses-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "academics")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Courses
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("chapters.index") && (
                    <li>
                      <NavLink
                        to="/chapter-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "academics")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Chapters
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("units.index") && (
                    <li>
                      <NavLink
                        to="/unit-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "academics")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Units
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            )}
            {userPermissions.includes("tutor.index") && (
              <li>
                <NavLink
                  to="/tutor-list"
                  className={({ isActive }) => (isActive ? "active-page" : "")}
                  onClick={closeDropdowns}
                >
                  <Icon icon="mdi:library-outline" className="menu-icon" />
                  <span>Tutors</span>
                </NavLink>
              </li>
            )}
            {userPermissions.includes("assignCourse.index") && (
              <li>
                <NavLink
                  to="/assigncourse-list"
                  className={({ isActive }) => (isActive ? "active-page" : "")}
                  onClick={closeDropdowns}
                >
                  <Icon icon="mdi:book-check-outline" className="menu-icon" />
                  <span>Assigned Courses</span>
                </NavLink>
              </li>
            )}
            {userPermissions.includes("batch.index") && (
              <li>
                <NavLink
                  to="/batch-list"
                  className={({ isActive }) => (isActive ? "active-page" : "")}
                  onClick={closeDropdowns}
                >
                  <Icon icon="mdi:shape-outline" className="menu-icon" />
                  <span>Batches</span>
                </NavLink>
              </li>
            )}
            {userPermissions.includes("assignCourse.index") && (
              <li>
                <NavLink
                  to="/enrolledcourse-list"
                  className={({ isActive }) => (isActive ? "active-page" : "")}
                  onClick={closeDropdowns}
                >
                  <Icon icon="mdi:book-check-outline" className="menu-icon" />
                  <span>Enrolled Courses</span>
                </NavLink>
              </li>
            )}
            {hasPermissionForDropdown([
              "student.index",
              "traders.index",
              "guests.index",
            ]) && (
              <li
                className={`dropdown ${openDropdown === "users" ? "open" : ""}`}
              >
                <Link to="#" onClick={() => toggleDropdown("users")}>
                  <Icon icon="mdi:account-outline" className="menu-icon" />
                  <span>Users</span>
                </Link>
                <ul className="sidebar-submenu">
                  {userPermissions.includes("student.index") && (
                    <li>
                      <NavLink
                        to="/student-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "users")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Students
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("traders.index") && (
                    <li>
                      <NavLink
                        to="/trader-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "users")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Traders
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("guests.index") && (
                    <li>
                      <NavLink
                        to="/guest-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "users")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Guests
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            )}
            {userPermissions.includes("student.index") && !userName?.startsWith("E2TMT") && (
              <li>
                <NavLink
                  to="/chat-list"
                  className={({ isActive }) => (isActive ? "active-page" : "")}
                  onClick={closeDropdowns}
                >
                  <Icon icon="mdi:chat-outline" className="menu-icon" />
                  <span>Chats</span>
                </NavLink>
              </li>
            )}
            {/* {userPermissions.includes("timetable.view") && (
              <li>
                <NavLink
                  to="/timetable-view"
                  className={({ isActive }) => (isActive ? "active-page" : "")}
                  onClick={closeDropdowns}
                >
                  <Icon icon="mdi:calendar-clock" className="menu-icon" />
                  <span>Timetable</span>
                </NavLink>
              </li>
            )} */}
            {hasPermissionForDropdown([
              "freecommunities.index",
              "coursecommunities.index",
              "highlights.index",
              "freevideos.index",
              "subscription.index",
              "membership.index",
              "student.index",
            ]) && (
              <li
                className={`dropdown ${
                  openDropdown === "app-data" ? "open" : ""
                }`}
              >
                <Link to="#" onClick={() => toggleDropdown("app-data")}>
                  <Icon icon="mdi:folder-outline" className="menu-icon" />
                  <span>App Data</span>
                </Link>
                <ul className="sidebar-submenu">
                  {userPermissions.includes("freecommunities.index") && (
                    <li>
                      <NavLink
                        to="/community-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "app-data")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Free Communities
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("coursecommunities.index") && (
                    <li>
                      <NavLink
                        to="/course-community-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "app-data")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Course Communities
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("highlights.index") && (
                    <li>
                      <NavLink
                        to="/highlight-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "app-data")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Highlights
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("promotion.index") && (
                    <li>
                      <NavLink
                        to="/promotion-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "app-data")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Promotion
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("freevideos.index") && (
                    <li>
                      <NavLink
                        to="/freevideo-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "app-data")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Free Videos
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("subscription.index") && (
                    <li>
                      <NavLink
                        to="/noticeboardCategory-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "app-data")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Noticeboard Category
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("noticeboard.index") && (
                    <li>
                      <NavLink
                        to="/noticeboard-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "app-data")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Noticeboard
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("freevideos.index") && (
                    <li>
                      <NavLink
                        to="/referrals-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "app-data")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Referrals
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("membership.index") && (
                    <li>
                      <NavLink
                        to="/membership-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "app-data")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Memberships
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("student.index") && (
                    <li>
                      <NavLink
                        to="/zoom-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "app-data")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Zoom Meetings
                      </NavLink>
                    </li>
                  )}
                  {/* {userPermissions.includes("student.index") && (
                    <li>
                      <NavLink
                        to="/directors-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "app-data")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Directors
                      </NavLink>
                    </li>
                  )} */}
                  {userPermissions.includes("membership.index") && (
                    <li>
                      <NavLink
                        to="/team-list"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "app-data")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Team
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            )}
            {hasPermissionForDropdown(["membership.index"]) && (
              <li
                className={`dropdown ${
                  openDropdown === "payments" ? "open" : ""
                }`}
              >
                <Link to="#" onClick={() => toggleDropdown("payments")}>
                  <Icon icon="mdi:currency-usd" className="menu-icon" />
                  <span>Payments</span>
                </Link>
                <ul className="sidebar-submenu">
                  {userPermissions.includes("membership.index") && (
                    <li>
                      <NavLink
                        to="/course-payments"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "payments")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Course Payments
                      </NavLink>
                    </li>
                  )}
                  {userPermissions.includes("membership.index") && (
                    <li>
                      <NavLink
                        to="/membership-payments"
                        className={({ isActive }) =>
                          isActive ? "active-page" : ""
                        }
                        onClick={(e) => handleSubmenuClick(e, "payments")}
                      >
                        <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                        Membership Payments
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            )}
            {/* {userPermissions.includes("courseverification.index") && (
              <li
                className={`dropdown ${
                  openDropdown === "course-verification" ? "open" : ""
                }`}
              >
                <Link
                  to="#"
                  onClick={() => toggleDropdown("course-verification")}
                >
                  <Icon icon="mdi:check-decagram" className="menu-icon" />
                  <span>Course Verification</span>
                </Link>
                <ul className="sidebar-submenu">
                  {loading ? (
                    <li>Loading batches...</li>
                  ) : error ? (
                    <li>{error}</li>
                  ) : (
                    batchNames.map((batch, index) => (
                      <li key={index}>
                        <NavLink
                          to={`/verification-list/${batch._id}`}
                          className={({ isActive }) =>
                            isActive ? "active-page" : ""
                          }
                          onClick={(e) =>
                            handleSubmenuClick(e, "course-verification")
                          }
                        >
                          <i className="ri-circle-fill circle-icon text-warning-main w-auto" />{" "}
                          {batch.batch_name}
                        </NavLink>
                      </li>
                    ))
                  )}
                </ul>
              </li>
            )} */}
            {userPermissions.includes("assessment.index") && (
              <li>
                <NavLink
                  to="/student-assessment-list"
                  className={({ isActive }) => (isActive ? "active-page" : "")}
                  onClick={closeDropdowns}
                >
                  <Icon icon="mdi:book-check-outline" className="menu-icon" />
                  <span>Student Assessment</span>
                </NavLink>
              </li>
            )}
          </ul>
        </div>
      </aside>

      <main
        className={sidebarActive ? "dashboard-main active" : "dashboard-main"}
      >
        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-4">
                <button
                  type="button"
                  className="sidebar-toggle"
                  onClick={sidebarControl}
                >
                  {sidebarActive ? (
                    <Icon
                      icon="iconoir:arrow-right"
                      className="icon text-2xl non-active"
                    />
                  ) : (
                    <Icon
                      icon="heroicons:bars-3-solid"
                      className="icon text-2xl non-active"
                    />
                  )}
                </button>
                <button
                  onClick={mobileMenuControl}
                  type="button"
                  className="sidebar-mobile-toggle"
                >
                  <Icon icon="heroicons:bars-3-solid" className="icon" />
                </button>
              </div>
            </div>
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-3">
                <ThemeToggleButton />
                <div className="dropdown">
                  <button
                    className="d-flex justify-content-center align-items-center rounded-circle"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <img
                      src="/assets/images/user-grid/user-grid-img14.png"
                      alt="image_user"
                      className="w-40-px h-40-px object-fit-cover rounded-circle"
                    />
                  </button>
                  <div className="dropdown-menu to-top dropdown-menu-sm">
                    <div className="py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
                      <div>
                        <span className="text-secondary-light fw-medium text-sm">
                          {name ? name : roleName}
                        </span>
                      </div>
                      <button type="button" className="hover-text-danger">
                        <Icon
                          icon="radix-icons:cross-1"
                          className="icon text-xl"
                        />
                      </button>
                    </div>
                    <ul className="to-top-list">
                      <li>
                        <Link
                          className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3"
                          onClick={handleLogout}
                        >
                          <Icon icon="lucide:power" className="icon text-xl" />{" "}
                          Log Out
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="dashboard-main-body"
          style={{ padding: "0 !important" }}
        >
          {children}
        </div>

        {/* Conditionally render the footer */}
        {!isChatView && (
          <footer className="d-footer">
            <div className="row align-items-center justify-content-between">
              <div className="col-auto">
                <p className="mb-0">© 2024 Educate2Trade. All Rights Reserved.</p>
              </div>
              <div className="col-auto">
                <p className="mb-0">
                  Made by <span className="text-primary-700">INCIEM</span>
                </p>
              </div>
            </div>
          </footer>
        )}
      </main>
    </section>
  );
};

export default MasterLayout;